/**
 * "Monatsabrechnung <Monat Jahr>" card with the five-step workflow.
 *
 * Honest by construction: each step shows a COUNTED value over ALL candidates of the
 * month, and a step is only marked done when the count actually supports it. The
 * numbers are deliberately NOT affected by the table's search box — a search narrows
 * what you are looking at, not what the month contains.
 *
 * The step labels were rewritten in C5.1. The previous set was misleading in ways that
 * mattered: "Daten sammeln" rendered `${total}/${total}`, a tautology that could only
 * ever read 100%; "Entwürfe generieren" counted checked candidates while generating no
 * draft whatsoever; and "Abschluss" collapsed to 0 as soon as a single row had failed,
 * reporting thirteen real Bexio drafts as zero work done.
 *
 * Two buttons, two different things, and the difference is the point of this slice:
 * "Entwürfe prüfen" runs the READ-ONLY preflight over every candidate and writes
 * nothing anywhere. The bulk CREATE button beside it stays rendered and DISABLED —
 * no safe bulk endpoint exists, and firing N single creates from the client would be
 * an unsafe simulation of one.
 */
import React from "react";

import { monthLabel } from "./bexioDraftLabels";

function Step({ index, title, value, state, isLast }) {
  const done = state === "done";
  const active = state === "active";

  const circle = done
    ? "border-emerald-500 bg-emerald-500 text-white dark:border-emerald-400 dark:bg-emerald-400 dark:text-emerald-950"
    : active
    ? "border-blue-500 bg-blue-500 text-white dark:border-blue-400 dark:bg-blue-400 dark:text-blue-950"
    : "border-input bg-muted text-muted-foreground";

  return (
    <li className="flex flex-1 flex-row items-start gap-3 sm:flex-col sm:items-center sm:gap-2">
      <div className="flex items-center gap-0 sm:w-full">
        <span className="hidden flex-1 sm:block" aria-hidden="true">
          <span
            className={`block h-0.5 ${
              index === 0 ? "bg-transparent" : done ? "bg-emerald-500/60" : "bg-input"
            }`}
          />
        </span>
        <span
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold ${circle}`}
        >
          {done ? "✓" : index + 1}
        </span>
        <span className="hidden flex-1 sm:block" aria-hidden="true">
          <span
            className={`block h-0.5 ${
              isLast ? "bg-transparent" : done ? "bg-emerald-500/60" : "bg-input"
            }`}
          />
        </span>
      </div>
      <div className="min-w-0 sm:text-center">
        <div className="text-xs font-medium text-foreground">{title}</div>
        <div className="text-xs tabular-nums text-muted-foreground">{value}</div>
      </div>
    </li>
  );
}

export default function BexioDraftMonthWorkflow({
  year,
  month,
  onMonthChange,
  steps,
  onRefresh,
  onCheckAll,
  loading,
  checking,
  checkProgress,
  creating,
  canCreate,
  createBlockedReason,
  candidateCount = 0,
}) {
  // Changing the month mid-create would let the confirmed figures and the submitted
  // period drift apart, so the picker is locked for the duration of the POST.
  const monthLocked = Boolean(creating);
  return (
    <section className="rounded-xl border border-input bg-card p-4 shadow-sm sm:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-base font-semibold text-foreground">
            Monatsabrechnung {monthLabel(year, month)}
          </h2>
          <div className="flex items-center gap-2">
            <label htmlFor="bexio-draft-month" className="sr-only">
              Abrechnungsmonat
            </label>
            <input
              id="bexio-draft-month"
              type="month"
              aria-label="Abrechnungsmonat"
              value={`${year}-${String(month).padStart(2, "0")}`}
              disabled={monthLocked}
              title={
                monthLocked ? "Während einer Erstellung gesperrt" : undefined
              }
              onChange={(e) => {
                const [y, m] = e.target.value.split("-");
                if (y && m) onMonthChange(Number(y), Number(m));
              }}
              className="rounded-lg border border-input bg-background px-2.5 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={onRefresh}
            disabled={loading || checking}
            className="rounded-lg border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Wird geladen …" : "Daten neu laden"}
          </button>
          {/* READ-ONLY. Runs the preflight GET for every candidate of the month —
              no ledger claim, no Bexio call, no write of any kind. */}
          {/* `creating` is in the guard because the confirm dialog does not trap
              focus and disables its own two buttons while submitting — so Tab skips
              straight past them to here. A sweep started mid-POST would write a
              result for the row the create is about to invalidate. */}
          <button
            type="button"
            onClick={onCheckAll}
            disabled={loading || checking || creating || candidateCount === 0}
            className="rounded-lg border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
          >
            {/* Progress belongs to the SWEEP only. A single-row check disables this
                button but must not relabel it "Prüfung läuft … 0/0", which would be
                a count of nothing dressed up as progress. */}
            {checkProgress
              ? `Prüfung läuft … ${checkProgress.done}/${checkProgress.total}`
              : "Entwürfe prüfen"}
          </button>
          {/* Visually present, deliberately inert: there is NO bulk endpoint, and a
              client-side loop over the single-create route would be an unsafe
              simulation of one. */}
          <button
            type="button"
            disabled
            title="Sammel-Erstellung folgt"
            aria-disabled="true"
            className="cursor-not-allowed rounded-lg bg-primary/40 px-3 py-1.5 text-sm font-medium text-primary-foreground opacity-70"
          >
            Alle READY-Entwürfe erstellen
          </button>
        </div>
      </div>

      <ol className="mt-5 flex flex-col gap-4 sm:flex-row sm:gap-2">
        {steps.map((s, i) => (
          <Step
            key={s.title}
            index={i}
            title={s.title}
            value={s.value}
            state={s.state}
            isLast={i === steps.length - 1}
          />
        ))}
      </ol>

      {!canCreate && createBlockedReason === "role" && (
        <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
          Sie können Entwürfe ansehen und prüfen, aber nicht erstellen — das
          Erstellen ist Administratoren vorbehalten.
        </p>
      )}
    </section>
  );
}
